import type React from 'react'
import { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../ui/card'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  AlertCircle,
} from 'lucide-react'
import type { DealFormData } from '../../../lib/types'
import { LOCKDROPS_VALUE } from '../../../lib/constants'
import { isValidWalletAddress as isValidWallet } from '../../../lib/validators'
import { FileUpload } from '../file-upload'
import useFileUploader from 'hooks/useFileUploader'

interface StepFundAllocationProps {
  formData: DealFormData
  onComplete: (data: Partial<DealFormData>) => void
  onUpdate: (data: Partial<DealFormData>) => void
  onBack: () => void
  headingId?: string
  onValidationError?: (errorMessage: string) => void
  onValidationErrors?: (errors: { id: string; label: string }[]) => void
  requestChanges?: Record<string, string>
}

export function StepFundAllocation({
  formData,
  onComplete,
  onUpdate,
  onBack,
  headingId,
  onValidationError,
  onValidationErrors,
  requestChanges,
}: StepFundAllocationProps) {
  const { uploadFile } = useFileUploader()
  const [localData, setLocalData] = useState({
    projectWallet: formData.projectWallet || '',
    curatorCommitmentAmount: formData.curatorCommitmentAmount || '',
    transactionLink: formData.transactionLink || '',
    supportingDocuments: formData.supportingDocuments || null,
    supportingDocumentsUrl: (formData as any).supportingDocumentsUrl || '',
    flatCommissionRate: formData.flatCommissionRate || '',
    carryCommissionRate: formData.carryCommissionRate || '',
    curatorCommissionWallet: formData.curatorCommissionWallet || '',
    commissionType: formData.commissionType || '',
    stablecoinCommission: formData.stablecoinCommission || '',
    tokenCommission: formData.tokenCommission || '',
    hasAssetManager: formData.hasAssetManager || '',
    assetManagerWallet: formData.assetManagerWallet || '',
    lockdropCuratorWallet: formData.lockdropCuratorWallet || '',
    lockdropPerformanceFeesType: formData.lockdropPerformanceFeesType || '',
    lockdropPerformanceFeesStablecoin:
      formData.lockdropPerformanceFeesStablecoin || '',
    lockdropPerformanceFeesToken: formData.lockdropPerformanceFeesToken || '',
    lockdropManagementFeesType: formData.lockdropManagementFeesType || '',
    lockdropManagementFeesStablecoin:
      formData.lockdropManagementFeesStablecoin || '',
    lockdropManagementFeesToken: formData.lockdropManagementFeesToken || '',
    otcSettlementWallet: formData.otcSettlementWallet || '',
  })

  const [supportUploading, setSupportUploading] = useState(false)
  const [supportUploadError, setSupportUploadError] = useState<
    string | undefined
  >()

  const [curatorCommitmentExpanded, setCuratorCommitmentExpanded] =
    useState(false)

  const [errors, setErrors] = useState<Record<string, string>>({})

  const [commissionType, setCommissionType] = useState<string>(
    formData.commissionType || '',
  )
  const [stablecoinCommission, setStablecoinCommission] = useState(
    formData.stablecoinCommission || '',
  )
  const [tokenCommission, setTokenCommission] = useState(
    formData.tokenCommission || '',
  )

  const [lockdropPerformanceFeesType, setLockdropPerformanceFeesType] =
    useState(formData.lockdropPerformanceFeesType || '')
  const [
    lockdropPerformanceFeesStablecoin,
    setLockdropPerformanceFeesStablecoin,
  ] = useState(formData.lockdropPerformanceFeesStablecoin || '')
  const [lockdropPerformanceFeesToken, setLockdropPerformanceFeesToken] =
    useState(formData.lockdropPerformanceFeesToken || '')
  const [lockdropManagementFeesType, setLockdropManagementFeesType] = useState(
    formData.lockdropManagementFeesType || '',
  )
  const [
    lockdropManagementFeesStablecoin,
    setLockdropManagementFeesStablecoin,
  ] = useState(formData.lockdropManagementFeesStablecoin || '')
  const [lockdropManagementFeesToken, setLockdropManagementFeesToken] =
    useState(formData.lockdropManagementFeesToken || '')

  const isValidCommissionRate = (rate: string) => {
    const num = Number.parseFloat(rate)
    return !isNaN(num) && num >= 0 && num <= 100
  }

  const clearError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev
      const next = { ...prev }
      delete next[field]
      return next
    })
  }

  const updateLocalField = (field: keyof typeof localData, value: string) => {
    setLocalData((prev) => ({ ...prev, [field]: value }))
    clearError(field)
    onUpdate({ [field]: value } as Partial<DealFormData>)
  }

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {}

    if (formData.salesType !== 'OTC Deal') {
      if (!localData.projectWallet)
        newErrors.projectWallet = 'Project wallet is required'
      else if (!isValidWallet(localData.projectWallet))
        newErrors.projectWallet = 'Please enter a valid ETH/EVM wallet address'
    } else {
      if (!localData.otcSettlementWallet)
        newErrors.otcSettlementWallet = 'OTC settlement wallet is required'
      else if (!isValidWallet(localData.otcSettlementWallet))
        newErrors.otcSettlementWallet =
          'Please enter a valid ETH/EVM wallet address'
    }

    if (formData.salesType === LOCKDROPS_VALUE) {
      if (!localData.hasAssetManager)
        newErrors.hasAssetManager = 'Please select an option for Asset Manager'
      else if (
        localData.hasAssetManager === 'yes' &&
        localData.assetManagerWallet &&
        !isValidWallet(localData.assetManagerWallet)
      )
        newErrors.assetManagerWallet =
          'Please enter a valid ETH/EVM wallet address'
    }

    if (formData.salesType !== LOCKDROPS_VALUE) {
      if (!commissionType)
        newErrors.commissionType = 'Commission type is required'
      else if (commissionType === 'stablecoins') {
        if (!stablecoinCommission)
          newErrors.stablecoinCommission = 'Commission rate is required'
        else if (!isValidCommissionRate(stablecoinCommission))
          newErrors.stablecoinCommission = 'Rate must be between 0-100%'
      } else if (commissionType === 'tokens') {
        if (!tokenCommission)
          newErrors.tokenCommission = 'Commission rate is required'
        else if (!isValidCommissionRate(tokenCommission))
          newErrors.tokenCommission = 'Rate must be between 0-100%'
      } else if (commissionType === 'both') {
        if (!stablecoinCommission)
          newErrors.stablecoinCommission = 'Stablecoin commission is required'
        else if (!isValidCommissionRate(stablecoinCommission))
          newErrors.stablecoinCommission = 'Rate must be between 0-100%'
        if (!tokenCommission)
          newErrors.tokenCommission = 'Token commission is required'
        else if (!isValidCommissionRate(tokenCommission))
          newErrors.tokenCommission = 'Rate must be between 0-100%'
      }

      if (!localData.curatorCommissionWallet)
        newErrors.curatorCommissionWallet =
          'Curator commission wallet is required'
      else if (!isValidWallet(localData.curatorCommissionWallet))
        newErrors.curatorCommissionWallet =
          'Please enter a valid ETH/EVM wallet address'
    }

    if (formData.salesType === LOCKDROPS_VALUE) {
      if (!localData.lockdropCuratorWallet)
        newErrors.lockdropCuratorWallet = 'Curator wallet is required'
      else if (!isValidWallet(localData.lockdropCuratorWallet))
        newErrors.lockdropCuratorWallet =
          'Please enter a valid ETH/EVM wallet address'

      if (!lockdropPerformanceFeesType)
        newErrors.lockdropPerformanceFeesType =
          'Performance fees type is required'
      else if (lockdropPerformanceFeesType === 'stablecoins') {
        if (!lockdropPerformanceFeesStablecoin)
          newErrors.lockdropPerformanceFeesStablecoin =
            'Performance fee rate is required'
        else if (!isValidCommissionRate(lockdropPerformanceFeesStablecoin))
          newErrors.lockdropPerformanceFeesStablecoin =
            'Rate must be between 0-100%'
      } else if (lockdropPerformanceFeesType === 'tokens') {
        if (!lockdropPerformanceFeesToken)
          newErrors.lockdropPerformanceFeesToken =
            'Performance fee rate is required'
        else if (!isValidCommissionRate(lockdropPerformanceFeesToken))
          newErrors.lockdropPerformanceFeesToken = 'Rate must be between 0-100%'
      } else if (lockdropPerformanceFeesType === 'both') {
        if (!lockdropPerformanceFeesStablecoin)
          newErrors.lockdropPerformanceFeesStablecoin =
            'Stablecoin performance fee rate is required'
        else if (!isValidCommissionRate(lockdropPerformanceFeesStablecoin))
          newErrors.lockdropPerformanceFeesStablecoin =
            'Rate must be between 0-100%'
        if (!lockdropPerformanceFeesToken)
          newErrors.lockdropPerformanceFeesToken =
            'Token performance fee rate is required'
        else if (!isValidCommissionRate(lockdropPerformanceFeesToken))
          newErrors.lockdropPerformanceFeesToken = 'Rate must be between 0-100%'
      }

      if (!lockdropManagementFeesType)
        newErrors.lockdropManagementFeesType =
          'Management fees type is required'
      else if (lockdropManagementFeesType === 'stablecoins') {
        if (!lockdropManagementFeesStablecoin)
          newErrors.lockdropManagementFeesStablecoin =
            'Management fee rate is required'
        else if (!isValidCommissionRate(lockdropManagementFeesStablecoin))
          newErrors.lockdropManagementFeesStablecoin =
            'Rate must be between 0-100%'
      } else if (lockdropManagementFeesType === 'tokens') {
        if (!lockdropManagementFeesToken)
          newErrors.lockdropManagementFeesToken =
            'Management fee rate is required'
        else if (!isValidCommissionRate(lockdropManagementFeesToken))
          newErrors.lockdropManagementFeesToken = 'Rate must be between 0-100%'
      } else if (lockdropManagementFeesType === 'both') {
        if (!lockdropManagementFeesStablecoin)
          newErrors.lockdropManagementFeesStablecoin =
            'Stablecoin management fee rate is required'
        else if (!isValidCommissionRate(lockdropManagementFeesStablecoin))
          newErrors.lockdropManagementFeesStablecoin =
            'Rate must be between 0-100%'
        if (!lockdropManagementFeesToken)
          newErrors.lockdropManagementFeesToken =
            'Token management fee rate is required'
        else if (!isValidCommissionRate(lockdropManagementFeesToken))
          newErrors.lockdropManagementFeesToken = 'Rate must be between 0-100%'
      }
    }

    setErrors(newErrors)
    const errorIds = Object.keys(newErrors)
    if (errorIds.length > 0) {
      const fieldLabels: Record<string, string> = {
        projectWallet: 'Project Wallet',
        ...(formData.salesType === LOCKDROPS_VALUE
          ? {
              hasAssetManager: 'Asset Manager',
              assetManagerWallet: 'Asset Manager Wallet',
            }
          : {}),
        lockdropCuratorWallet: 'Lockdrop Curator Wallet',
        lockdropPerformanceFeesType: 'Performance Fees Type',
        lockdropPerformanceFeesStablecoin: 'Performance Fees (Stablecoins)',
        lockdropPerformanceFeesToken: 'Performance Fees (Tokens)',
        lockdropManagementFeesType: 'Management Fees Type',
        lockdropManagementFeesStablecoin: 'Management Fees (Stablecoins)',
        lockdropManagementFeesToken: 'Management Fees (Tokens)',
        curatorCommitmentAmount: 'Curator Commitment',
        transactionLink: 'Transaction Link',
        ...(formData.salesType === 'OTC Deal'
          ? {
              otcSettlementWallet: 'SPV (OTC Settlement) Wallet',
            }
          : {}),
        ...(formData.salesType !== LOCKDROPS_VALUE
          ? {
              commissionType: 'Commission Type',
              stablecoinCommission: 'Stablecoin Commission',
              tokenCommission: 'Token Commission',
              curatorCommissionWallet: 'Curator Commission Wallet',
            }
          : {}),
      }

      const mappedErrors = errorIds.map((id) => ({
        id,
        label: fieldLabels[id] || id,
      }))

      if (onValidationErrors) {
        onValidationErrors(mappedErrors)
      } else {
        onValidationError?.(
          'Please complete all required fields before continuing.',
        )
      }
      return
    }
    onValidationErrors?.([])
    onValidationError?.('')
    onComplete(localData)
  }

  const handleSupportingDocSelected = (file: File | null) => {
    setLocalData((prev) => ({ ...prev, supportingDocuments: file }))
    onUpdate({ supportingDocuments: file } as any)
    if (!file) {
      setSupportUploading(false)
      setSupportUploadError(undefined)
      setLocalData((prev) => ({ ...prev, supportingDocumentsUrl: '' }))
      onUpdate({ supportingDocumentsUrl: '' } as any)
      return
    }
    setSupportUploading(true)
    setSupportUploadError(undefined)
    uploadFile(
      { file, path: `submission/supporting-${Date.now()}-${file.name}` },
      (res: { url: string }) => {
        setLocalData((prev) => ({ ...prev, supportingDocumentsUrl: res.url }))
        onUpdate({ supportingDocumentsUrl: res.url } as any)
        setSupportUploading(false)
        setSupportUploadError(undefined)
      },
      () => {
        setSupportUploading(false)
        setSupportUploadError('Upload failed. Please try again.')
      },
    )
  }

  return (
    <div className="space-y-6">
      {formData.salesType === LOCKDROPS_VALUE && (
        <Card>
          <CardHeader>
            <CardTitle>Asset Manager</CardTitle>
            <CardDescription>
              An Asset Manager is a third-party entity authorized to custody
              funds or execute token distribution on behalf of this sale. Select
              "Yes" if a manager will be involved, and provide their wallet
              address.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Select
                value={localData.hasAssetManager}
                onValueChange={(value) => {
                  updateLocalField('hasAssetManager', value)
                  if (value === 'no') {
                    setLocalData((prev) => ({
                      ...prev,
                      assetManagerWallet: '',
                    }))
                    onUpdate({
                      assetManagerWallet: '',
                    } as Partial<DealFormData>)
                  }
                }}
              >
                <SelectTrigger
                  id="hasAssetManager"
                  className={errors.hasAssetManager ? 'border-red-500' : ''}
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex flex-col gap-1">
                {errors.hasAssetManager && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.hasAssetManager}
                  </p>
                )}
                {requestChanges?.hasAssetManager && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.hasAssetManager}
                  </p>
                )}
              </div>
            </div>

            {localData.hasAssetManager === 'yes' && (
              <div className="space-y-2">
                <Label
                  htmlFor="assetManagerWallet"
                  className="text-sm font-medium text-neutral-800"
                >
                  Asset Manager Wallet Address
                </Label>
                <Input
                  id="assetManagerWallet"
                  value={localData.assetManagerWallet}
                  onChange={(e) =>
                    updateLocalField('assetManagerWallet', e.target.value)
                  }
                  placeholder="0x..."
                  className={`${
                    errors.assetManagerWallet ? 'border-red-500' : ''
                  }`}
                />
                <div className="mt-3 flex items-start gap-2 rounded-sm border border-yellow-500 bg-yellow-50 p-2 text-xs text-yellow-500">
                  <p>
                    You can add the asset manager's wallet address later. This
                    information will be required before submission.
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  {errors.assetManagerWallet && (
                    <p className="text-xs text-status-error inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.assetManagerWallet}
                    </p>
                  )}
                  {requestChanges?.assetManagerWallet && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.assetManagerWallet}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {formData.salesType !== 'OTC Deal' && (
        <Card>
          <CardHeader>
            <CardTitle>Project Receiving Wallet</CardTitle>
            <CardDescription>
              This is the Project's address that will receive funding. Once
              deposits start, this wallet cannot be changed. Double-check before
              submitting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Input
                id="projectWallet"
                value={localData.projectWallet}
                onChange={(e) =>
                  updateLocalField('projectWallet', e.target.value)
                }
                placeholder="0x..."
                className={`${errors.projectWallet ? 'border-red-500' : ''}`}
              />
              <div className="flex flex-col gap-1">
                {errors.projectWallet && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.projectWallet}
                  </p>
                )}
                {requestChanges?.projectWallet && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.projectWallet}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {formData.salesType === LOCKDROPS_VALUE && (
        <Card>
          <CardHeader>
            <CardTitle>Lockdrop Curator Fees</CardTitle>
            <CardDescription>
              Configure the fees for the lockdrop curator.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="lockdropCuratorWallet"
                className="text-sm font-medium text-neutral-800"
              >
                Curator Receiving Wallet Address
              </Label>
              <p className="text-xs text-neutral-600">
                This address will receive curator fees and cannot be changed
                later.
              </p>
              <Input
                id="lockdropCuratorWallet"
                value={localData.lockdropCuratorWallet}
                onChange={(e) =>
                  updateLocalField('lockdropCuratorWallet', e.target.value)
                }
                placeholder="0x..."
                className={`${
                  errors.lockdropCuratorWallet ? 'border-red-500' : ''
                }`}
              />
              <div className="flex flex-col gap-1">
                {errors.lockdropCuratorWallet && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.lockdropCuratorWallet}
                  </p>
                )}
                {requestChanges?.lockdropCuratorWallet && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.lockdropCuratorWallet}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-neutral-800">
                Performance Fees
              </Label>
              <p className="text-xs text-neutral-600">
                Percentage of yield generated by the Lockdrop strategy
              </p>
              <Select
                value={lockdropPerformanceFeesType}
                onValueChange={(value) => {
                  setLockdropPerformanceFeesType(value)
                  setLocalData((prev) => ({
                    ...prev,
                    lockdropPerformanceFeesType: value,
                  }))
                  onUpdate({
                    lockdropPerformanceFeesType: value,
                  } as Partial<DealFormData>)
                }}
              >
                <SelectTrigger
                  id="lockdropPerformanceFeesType"
                  className={
                    errors.lockdropPerformanceFeesType ? 'border-red-500' : ''
                  }
                >
                  <SelectValue placeholder="Select asset type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stablecoins">Stablecoins only</SelectItem>
                  <SelectItem value="tokens">Tokens only</SelectItem>
                  <SelectItem value="both">
                    Both stablecoins and tokens
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex flex-col gap-1">
                {errors.lockdropPerformanceFeesType && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.lockdropPerformanceFeesType}
                  </p>
                )}
              </div>

              {lockdropPerformanceFeesType === 'stablecoins' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="lockdropPerformanceFeesStablecoin"
                      type="number"
                      value={lockdropPerformanceFeesStablecoin}
                      onChange={(e) => {
                        setLockdropPerformanceFeesStablecoin(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          lockdropPerformanceFeesStablecoin: e.target.value,
                        }))
                        onUpdate({
                          lockdropPerformanceFeesStablecoin: e.target.value,
                        } as Partial<DealFormData>)
                      }}
                      placeholder="e.g., 10"
                      className={`pr-8 ${
                        errors.lockdropPerformanceFeesStablecoin
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.lockdropPerformanceFeesStablecoin && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropPerformanceFeesStablecoin}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {lockdropPerformanceFeesType === 'tokens' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="lockdropPerformanceFeesToken"
                      type="number"
                      value={lockdropPerformanceFeesToken}
                      onChange={(e) => {
                        setLockdropPerformanceFeesToken(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          lockdropPerformanceFeesToken: e.target.value,
                        }))
                        onUpdate({
                          lockdropPerformanceFeesToken: e.target.value,
                        } as Partial<DealFormData>)
                      }}
                      placeholder="e.g., 10"
                      className={`pr-8 ${
                        errors.lockdropPerformanceFeesToken
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.lockdropPerformanceFeesToken && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropPerformanceFeesToken}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {lockdropPerformanceFeesType === 'both' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-800">
                      Stablecoins
                    </Label>
                    <div className="relative">
                      <Input
                        id="lockdropPerformanceFeesStablecoin"
                        type="number"
                        value={lockdropPerformanceFeesStablecoin}
                        onChange={(e) => {
                          setLockdropPerformanceFeesStablecoin(e.target.value)
                          setLocalData((prev) => ({
                            ...prev,
                            lockdropPerformanceFeesStablecoin: e.target.value,
                          }))
                          onUpdate({
                            lockdropPerformanceFeesStablecoin: e.target.value,
                          } as Partial<DealFormData>)
                        }}
                        placeholder="e.g., 10"
                        className={`pr-8 ${
                          errors.lockdropPerformanceFeesStablecoin
                            ? 'border-red-500'
                            : ''
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        %
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {errors.lockdropPerformanceFeesStablecoin && (
                        <p className="text-xs text-status-error inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.lockdropPerformanceFeesStablecoin}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-800">
                      Tokens
                    </Label>
                    <div className="relative">
                      <Input
                        id="lockdropPerformanceFeesToken"
                        type="number"
                        value={lockdropPerformanceFeesToken}
                        onChange={(e) => {
                          setLockdropPerformanceFeesToken(e.target.value)
                          setLocalData((prev) => ({
                            ...prev,
                            lockdropPerformanceFeesToken: e.target.value,
                          }))
                          onUpdate({
                            lockdropPerformanceFeesToken: e.target.value,
                          } as Partial<DealFormData>)
                        }}
                        placeholder="e.g., 10"
                        className={`pr-8 ${
                          errors.lockdropPerformanceFeesToken
                            ? 'border-red-500'
                            : ''
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        %
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {errors.lockdropPerformanceFeesToken && (
                        <p className="text-xs text-status-error inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.lockdropPerformanceFeesToken}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-neutral-800">
                Management Fees
              </Label>
              <p className="text-xs text-neutral-600">
                Percentage of total amounts raised / total incentives
              </p>
              <Select
                value={lockdropManagementFeesType}
                onValueChange={(value) => {
                  setLockdropManagementFeesType(value)
                  setLocalData((prev) => ({
                    ...prev,
                    lockdropManagementFeesType: value,
                  }))
                  onUpdate({
                    lockdropManagementFeesType: value,
                  } as Partial<DealFormData>)
                }}
              >
                <SelectTrigger
                  id="lockdropManagementFeesType"
                  className={
                    errors.lockdropManagementFeesType ? 'border-red-500' : ''
                  }
                >
                  <SelectValue placeholder="Select asset type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stablecoins">Stablecoins only</SelectItem>
                  <SelectItem value="tokens">Tokens only</SelectItem>
                  <SelectItem value="both">
                    Both stablecoins and tokens
                  </SelectItem>
                </SelectContent>
              </Select>
              <div className="flex flex-col gap-1">
                {errors.lockdropManagementFeesType && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.lockdropManagementFeesType}
                  </p>
                )}
              </div>

              {lockdropManagementFeesType === 'stablecoins' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="lockdropManagementFeesStablecoin"
                      type="number"
                      value={lockdropManagementFeesStablecoin}
                      onChange={(e) => {
                        setLockdropManagementFeesStablecoin(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          lockdropManagementFeesStablecoin: e.target.value,
                        }))
                        onUpdate({
                          lockdropManagementFeesStablecoin: e.target.value,
                        } as Partial<DealFormData>)
                      }}
                      placeholder="e.g., 15"
                      className={`pr-8 ${
                        errors.lockdropManagementFeesStablecoin
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.lockdropManagementFeesStablecoin && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropManagementFeesStablecoin}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {lockdropManagementFeesType === 'tokens' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="lockdropManagementFeesToken"
                      type="number"
                      value={lockdropManagementFeesToken}
                      onChange={(e) => {
                        setLockdropManagementFeesToken(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          lockdropManagementFeesToken: e.target.value,
                        }))
                        onUpdate({
                          lockdropManagementFeesToken: e.target.value,
                        } as Partial<DealFormData>)
                      }}
                      placeholder="e.g., 15"
                      className={`pr-8 ${
                        errors.lockdropManagementFeesToken
                          ? 'border-red-500'
                          : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.lockdropManagementFeesToken && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.lockdropManagementFeesToken}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {lockdropManagementFeesType === 'both' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-800">
                      Stablecoins
                    </Label>
                    <div className="relative">
                      <Input
                        id="lockdropManagementFeesStablecoin"
                        type="number"
                        value={lockdropManagementFeesStablecoin}
                        onChange={(e) => {
                          setLockdropManagementFeesStablecoin(e.target.value)
                          setLocalData((prev) => ({
                            ...prev,
                            lockdropManagementFeesStablecoin: e.target.value,
                          }))
                          onUpdate({
                            lockdropManagementFeesStablecoin: e.target.value,
                          } as Partial<DealFormData>)
                        }}
                        placeholder="e.g., 15"
                        className={`pr-8 ${
                          errors.lockdropManagementFeesStablecoin
                            ? 'border-red-500'
                            : ''
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        %
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {errors.lockdropManagementFeesStablecoin && (
                        <p className="text-xs text-status-error inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.lockdropManagementFeesStablecoin}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-neutral-800">
                      Tokens
                    </Label>
                    <div className="relative">
                      <Input
                        id="lockdropManagementFeesToken"
                        type="number"
                        value={lockdropManagementFeesToken}
                        onChange={(e) => {
                          setLockdropManagementFeesToken(e.target.value)
                          setLocalData((prev) => ({
                            ...prev,
                            lockdropManagementFeesToken: e.target.value,
                          }))
                          onUpdate({
                            lockdropManagementFeesToken: e.target.value,
                          } as Partial<DealFormData>)
                        }}
                        placeholder="e.g., 15"
                        className={`pr-8 ${
                          errors.lockdropManagementFeesToken
                            ? 'border-red-500'
                            : ''
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        %
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      {errors.lockdropManagementFeesToken && (
                        <p className="text-xs text-status-error inline-flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.lockdropManagementFeesToken}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {formData.salesType === 'OTC Deal' && (
        <Card>
          <CardHeader>
            <CardTitle>OTC Settlement</CardTitle>
            <CardDescription>
              Configure the settlement wallet for OTC fund withdrawal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="otcSettlementWallet"
                className="text-sm font-medium text-neutral-800"
              >
                SPV (OTC Settlement) Wallet
              </Label>
              <Input
                id="otcSettlementWallet"
                value={localData.otcSettlementWallet}
                onChange={(e) =>
                  updateLocalField('otcSettlementWallet', e.target.value)
                }
                placeholder="0x..."
                className={`${
                  errors.otcSettlementWallet ? 'border-red-500' : ''
                }`}
              />
              <p className="text-xs text-neutral-600">
                Enter the wallet address used for OTC fund withdrawal.
              </p>
              <div className="flex flex-col gap-1">
                {errors.otcSettlementWallet && (
                  <p className="text-xs text-status-error inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {errors.otcSettlementWallet}
                  </p>
                )}
                {requestChanges?.otcSettlementWallet && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {requestChanges.otcSettlementWallet}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {formData.salesType !== LOCKDROPS_VALUE && (
        <Card>
          <CardHeader>
            <CardTitle>Curator Commission Rate</CardTitle>
            <CardDescription>
              CURATED applies platform fees ranging from 3 to 5% in stablecoins
              depending on the sale terms.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-1">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-neutral-800">
                  Commissions
                </Label>
                <Select
                  value={commissionType}
                  onValueChange={(value) => {
                    setCommissionType(value)
                    setLocalData((prev) => ({ ...prev, commissionType: value }))
                    onUpdate({ commissionType: value } as any)
                  }}
                >
                  <SelectTrigger
                    id="commissionType"
                    className={errors.commissionType ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Select asset type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stablecoins">
                      Stablecoins fees
                    </SelectItem>
                    <SelectItem value="tokens">Tokens fees</SelectItem>
                    <SelectItem value="both">
                      Both stablecoins & tokens
                    </SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex flex-col gap-1">
                  {errors.commissionType && (
                    <p className="text-xs text-status-error inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.commissionType}
                    </p>
                  )}
                  {requestChanges?.commissionType && (
                    <p className="text-status-error text-xs inline-flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {requestChanges.commissionType}
                    </p>
                  )}
                </div>
              </div>

              {commissionType === 'stablecoins' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="stablecoinCommission"
                      type="number"
                      value={stablecoinCommission}
                      onChange={(e) => {
                        setStablecoinCommission(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          stablecoinCommission: e.target.value,
                        }))
                        onUpdate({
                          stablecoinCommission: e.target.value,
                        } as any)
                      }}
                      placeholder="e.g., 2.5"
                      className={`pr-8 ${
                        errors.stablecoinCommission ? 'border-red-500' : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">
                    Portion of fees collected in stablecoins (0–100%).
                  </p>
                  <div className="flex flex-col gap-1">
                    {errors.stablecoinCommission && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.stablecoinCommission}
                      </p>
                    )}
                    {requestChanges?.stablecoinCommission && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.stablecoinCommission}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {commissionType === 'tokens' && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      id="tokenCommission"
                      type="number"
                      value={tokenCommission}
                      onChange={(e) => {
                        setTokenCommission(e.target.value)
                        setLocalData((prev) => ({
                          ...prev,
                          tokenCommission: e.target.value,
                        }))
                        onUpdate({ tokenCommission: e.target.value } as any)
                      }}
                      placeholder="e.g., 2.5"
                      className={`pr-8 ${
                        errors.tokenCommission ? 'border-red-500' : ''
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                      %
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500">
                    Portion of fees collected in project tokens (0–100%).
                  </p>
                  <div className="flex flex-col gap-1">
                    {errors.tokenCommission && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.tokenCommission}
                      </p>
                    )}
                    {requestChanges?.tokenCommission && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.tokenCommission}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {commissionType === 'both' && (
                <div>
                  <div className="grid md:grid-cols-2 gap-4 mb-2">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-neutral-800">
                        Stablecoins
                      </Label>
                      <div className="relative">
                        <Input
                          id="stablecoinCommission"
                          type="number"
                          value={stablecoinCommission}
                          onChange={(e) => {
                            setStablecoinCommission(e.target.value)
                            onUpdate({
                              stablecoinCommission: e.target.value,
                            } as any)
                          }}
                          placeholder="e.g., 2.5"
                          className={`pr-8 ${
                            errors.stablecoinCommission ? 'border-red-500' : ''
                          }`}
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                          %
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-neutral-800">
                        Tokens
                      </Label>
                      <div className="relative">
                        <Input
                          id="tokenCommission"
                          type="number"
                          value={tokenCommission}
                          onChange={(e) => {
                            setTokenCommission(e.target.value)
                            onUpdate({ tokenCommission: e.target.value } as any)
                          }}
                          placeholder="e.g., 2.5"
                          className={`pr-8 ${
                            errors.tokenCommission ? 'border-red-500' : ''
                          }`}
                        />
                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <p className="text-xs text-neutral-500">
                      Stablecoins: Stablecoins upfront fee as a % of the total
                      amount raised.
                    </p>
                    <p className="text-xs text-neutral-500">
                      Tokens: Token-based fees as a % of the total tokens sold.
                      The same vesting terms of the sale should apply.
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {errors.stablecoinCommission && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.stablecoinCommission}
                      </p>
                    )}
                    {requestChanges?.stablecoinCommission && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.stablecoinCommission}
                      </p>
                    )}
                    {errors.tokenCommission && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.tokenCommission}
                      </p>
                    )}
                    {requestChanges?.tokenCommission && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.tokenCommission}
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="h-2" />
              <div className="border-t pt-6 space-y-2">
                <div>
                  <Label
                    htmlFor="curatorCommissionWallet"
                    className="text-base font-medium text-neutral-800"
                  >
                    Curator Commission Receiving Wallet
                  </Label>
                  <p className="text-sm text-neutral-600 pb-6">
                    This is the Curator's address that will receive commissions.
                    It cannot be changed later. Double-check before submitting.
                  </p>
                  <Input
                    id="curatorCommissionWallet"
                    value={localData.curatorCommissionWallet}
                    onChange={(e) =>
                      updateLocalField(
                        'curatorCommissionWallet',
                        e.target.value,
                      )
                    }
                    placeholder="0x..."
                    className={`${
                      errors.curatorCommissionWallet ? 'border-red-500' : ''
                    }`}
                  />
                  <div className="flex flex-col gap-1">
                    {errors.curatorCommissionWallet && (
                      <p className="text-xs text-status-error inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {errors.curatorCommissionWallet}
                      </p>
                    )}
                    {requestChanges?.curatorCommissionWallet && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.curatorCommissionWallet}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {formData.salesType !== 'OTC Deal' && (
        <Collapsible
          open={curatorCommitmentExpanded}
          onOpenChange={setCuratorCommitmentExpanded}
        >
          <Card>
            <CardHeader>
              <CollapsibleTrigger asChild>
                <button className="flex items-center justify-between w-full text-left">
                  <CardTitle>Curator Commitment Amount (Optional)</CardTitle>
                  {curatorCommitmentExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label
                    htmlFor="curatorCommitment"
                    className="text-sm font-medium text-neutral-800"
                  >
                    Amount (USD)
                  </Label>
                  <Input
                    id="curatorCommitmentAmount"
                    type="number"
                    value={localData.curatorCommitmentAmount}
                    onChange={(e) =>
                      updateLocalField(
                        'curatorCommitmentAmount',
                        e.target.value,
                      )
                    }
                    placeholder="Enter amount (e.g., 10,000)"
                  />
                  <p className="text-xs text-neutral-400">
                    Amount the curator commits to invest, in USD.
                  </p>
                  <div className="flex flex-col gap-1">
                    {requestChanges?.curatorCommitmentAmount && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.curatorCommitmentAmount}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="transactionLink"
                    className="text-sm font-normal"
                  >
                    On-chain Transaction Link
                  </Label>
                  <Input
                    id="transactionLink"
                    type="url"
                    value={localData.transactionLink}
                    onChange={(e) =>
                      updateLocalField('transactionLink', e.target.value)
                    }
                    placeholder="https://..."
                  />
                  <div className="flex flex-col gap-1">
                    {requestChanges?.transactionLink && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.transactionLink}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-normal">
                    Supporting Documents
                  </Label>
                  <FileUpload
                    mode="document"
                    accept="application/pdf"
                    buttonLabel="Upload PDF"
                    helperText="PDF only • Max 25 MB • No Docsend links"
                    maxSizeMB={25}
                    errorStrings={{
                      fileCount: 'Please upload one file.',
                      fileType: 'PDF only. Please upload a .pdf file.',
                      fileSize: 'File is too large (max 25 MB).',
                      unreadable:
                        "We couldn't read this file. Please try another file.",
                      password:
                        'This PDF is password protected. Please upload an unlocked file.',
                      empty: 'The file appears to be empty.',
                      generic: 'Upload failed. Please try again.',
                      linkDropNotAllowed:
                        'Links are not accepted. Please upload a file.',
                    }}
                    ariaLabel="Upload supporting documents file"
                    onFileSelect={handleSupportingDocSelected}
                    currentFile={localData.supportingDocuments}
                    externalUploading={supportUploading}
                    externalError={supportUploadError}
                  />
                  <div className="flex flex-col gap-1">
                    {requestChanges?.supportingDocuments && (
                      <p className="text-status-error text-xs inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {requestChanges.supportingDocuments}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          size="md"
          className="flex items-center gap-2 bg-transparent px-5"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="brand"
          size="md"
          className="flex items-center gap-2 px-5"
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
